// src/components/SubmitTime/components/SelectorsSection.tsx
// Sub-component for all selector dropdowns

import React from 'react';
import Select from 'react-select';
import type { StylesConfig, SingleValue } from 'react-select';
import { isSubmitTimeDark, ST_DARK } from './selectDarkMode';
import type {
  LayerItem,
  PhaseItem,
  ProjectItem,
  TypeItem,
  TaskItem,
  Team,
  ValidationState,
} from '../types';

interface Option<T = any> {
  value: number | string;
  label: string;
  data: T;
}

// Styles matching the project's ComboBoxField migration pattern.
// Note: menuPortalTarget={document.body} is used on every Select so the
// dropdown menu is rendered completely outside the modal DOM — this avoids
// any CSS inheritance from SubmitTime.css legacy .Select-* rules that
// collapse line-height / font-size on child elements.
const selectStyles: StylesConfig<Option, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: '32px',
    height: '32px',
    borderRadius: '2px',
    boxShadow: 'none',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    ...(isSubmitTimeDark() ? { backgroundColor: ST_DARK.controlBg } : {}),
    borderColor: state.isFocused
      ? (isSubmitTimeDark() ? ST_DARK.borderFocus : '#66afe9')
      : (isSubmitTimeDark() ? ST_DARK.border : '#ccc'),
    '&:hover': { borderColor: isSubmitTimeDark() ? ST_DARK.borderFocus : '#66afe9' },
  }),
  valueContainer: base => ({
    ...base,
    height: '32px',
    padding: '0 8px',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    lineHeight: 'normal',
    ...(isSubmitTimeDark() ? { color: ST_DARK.text } : {}),
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({ ...base, padding: '4px 6px' }),
  clearIndicator: base => ({ ...base, padding: '4px' }),
  placeholder: base => ({
    ...base,
    fontSize: '12px',
    lineHeight: 'normal',
    color: isSubmitTimeDark() ? ST_DARK.placeholder : '#aaa',
  }),
  singleValue: base => ({
    ...base,
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    lineHeight: 'normal',
    color: isSubmitTimeDark() ? ST_DARK.text : 'rgb(158, 149, 135)',
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 1000200,
  }),
  menu: base => ({
    ...base,
    marginTop: 0,
    borderRadius: '2px',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    ...(isSubmitTimeDark()
      ? { backgroundColor: ST_DARK.menuBg, border: `1px solid ${ST_DARK.border}` }
      : {}),
  }),
  menuList: base => ({
    ...base,
    padding: 0,
    maxHeight: '200px',
  }),
  option: (_base, state) => ({
    display: 'block',
    boxSizing: 'border-box',
    width: '100%',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    lineHeight: '1.4',
    padding: '6px 10px',
    backgroundColor: state.isFocused
      ? ST_DARK.optionFocusBg
      : (isSubmitTimeDark() ? ST_DARK.menuBg : '#fff'),
    color: state.isFocused
      ? ST_DARK.optionFocusText
      : (isSubmitTimeDark() ? ST_DARK.optionText : '#333'),
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
};

interface SelectorsSectionProps {
  dataLayers: LayerItem[];
  dataPhases: PhaseItem[];
  dataProjects: ProjectItem[];
  dataType: TypeItem[];
  dataTasks: TaskItem[];
  defaultTeams: Team[];
  selectedId: LayerItem | null;
  phaseSelected: PhaseItem | null;
  projectSelected: ProjectItem | null;
  typeSelected: TypeItem | null;
  taskSelected: TaskItem | null;
  selectedTeam: Team | null;
  valForm: ValidationState;
  onLayerChange: (layer: LayerItem | null) => void;
  onPhaseChange: (phase: PhaseItem | null) => void;
  onProjectChange: (project: ProjectItem | null) => void;
  onTypeChange: (type: TypeItem | null) => void;
  onTaskChange: (task: TaskItem | null) => void;
  onTeamChange: (team: Team | null) => void;
  isLoadingLayers?: boolean;
  isLoadingPhases?: boolean;
  isLoadingProjects?: boolean;
  isLoadingTypes?: boolean;
  isLoadingTasks?: boolean;
  isLoadingTeams?: boolean;
  showTeams?: boolean;
  customKey?: number;
}

export const SelectorsSection: React.FC<SelectorsSectionProps> = ({
  dataLayers,
  dataPhases,
  dataProjects,
  dataType,
  dataTasks,
  defaultTeams,
  selectedId,
  phaseSelected,
  projectSelected,
  typeSelected,
  taskSelected,
  selectedTeam,
  valForm,
  onLayerChange,
  onPhaseChange,
  onProjectChange,
  onTypeChange,
  onTaskChange,
  onTeamChange,
  isLoadingLayers = false,
  isLoadingPhases = false,
  isLoadingProjects = false,
  isLoadingTypes = false,
  isLoadingTasks = false,
  isLoadingTeams = false,
  showTeams = false,
  customKey = 0,
}) =>
{
  // Build option arrays
  const layerOptions: Option<LayerItem>[] = dataLayers.map(l => ({
    value: l.LayerId,
    label: l.Name,
    data: l,
  }));

  // ConversationPhaseId identifies the phase on this card (PhaseId may repeat or be 0)
  const phaseKey = (p: PhaseItem) => p.ConversationPhaseId ?? p.PhaseId;
  const phaseOptions: Option<PhaseItem>[] = dataPhases.map(p => ({
    value: phaseKey(p),
    label: (p as any).Title || p.PhaseName || p.Name || '',
    data: p,
  }));

  const projectOptions: Option<ProjectItem>[] = dataProjects.map(p => ({
    value: p.ProjectId,
    label: p.ProjectName || p.Name || '',
    data: p,
  }));

  const typeOptions: Option<TypeItem>[] = dataType.map(t => ({
    value: t.ConfigValueID ?? 0,
    label: t.Text,
    data: t,
  }));

  const taskOptions: Option<TaskItem>[] = dataTasks.map(t => ({
    value: t.ConversationId,
    label: t.Name || t.Title || '',
    data: t,
  }));

  const teamOptions: Option<Team>[] = defaultTeams.map(t => ({
    value: t.TeamId,
    label: t.Team || t.TeamName || t.Name || '',
    data: t,
  }));

  // Resolve current selected options
  const selectedLayerOption = selectedId
    ? (layerOptions.find(o => o.value === selectedId.LayerId) ?? null)
    : null;

  const selectedPhaseOption = phaseSelected
    ? (phaseOptions.find(o => o.value === phaseKey(phaseSelected)) ?? null)
    : null;

  const selectedProjectOption = projectSelected
    ? (projectOptions.find(o => o.value === projectSelected.ProjectId) ?? null)
    : null;

  const selectedTypeOption = typeSelected
    ? (typeOptions.find(o => o.value === typeSelected.ConfigValueID) ?? null)
    : null;

  const selectedTaskOption = taskSelected
    ? (taskOptions.find(o => o.value === taskSelected.ConversationId) ?? null)
    : null;

  // Default team: match by TeamId so the reference is always from the options list
  const selectedTeamOption = selectedTeam
    ? (teamOptions.find(o => o.value === selectedTeam.TeamId) ?? null)
    : null;

  const commonProps = {
    styles: selectStyles as StylesConfig<Option, false>,
    menuPortalTarget: document.body,
    menuPlacement: 'auto' as const,
    isClearable: true,
    isSearchable: true,
  };

  // Phases combo: after Layer in conversation mode, after Task in standalone mode (/Calendar)
  const phasesBlock = valForm.showPhases && (
    <div className="corgee-layout-tableSubmitTimet">
      <div className="corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime">
        <span>Phases</span>
      </div>
      <div className="corgee-layout-table-cellSubmitTimet">
        <Select<Option>
          {...commonProps}
          instanceId={`phase-${customKey}`}
          options={phaseOptions}
          value={selectedPhaseOption}
          onChange={(opt: SingleValue<Option>) => onPhaseChange(opt?.data ?? null)}
          isDisabled={valForm.disabledPhases || isLoadingPhases}
          isLoading={isLoadingPhases}
          placeholder=""
        />
        {!isLoadingPhases && dataPhases.length === 0 ? (
          <span className="submit-required-field" style={{ fontSize: '11px' }}>
            This card has phases but none are available to post time
          </span>
        ) : (
          valForm.validPhases && (
            <span className="submit-required-field" style={{ fontSize: '11px' }}>
              Phase is required
            </span>
          )
        )}
      </div>
    </div>
  );

  return (
    <div className="selectors-section">
      {/* Team — shown only when timeTrackingTeam is true */}
      {showTeams && defaultTeams.length > 0 && (
        <div className="corgee-layout-tableSubmitTimet">
          <div className="corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime">
            <span>Team</span>
          </div>
          <div className="corgee-layout-table-cellSubmitTimet">
            <Select<Option>
              {...commonProps}
              instanceId={`team-${customKey}`}
              options={teamOptions}
              value={selectedTeamOption}
              onChange={(opt: SingleValue<Option>) => onTeamChange(opt?.data ?? null)}
              isLoading={isLoadingTeams}
              placeholder=""
            />
          </div>
        </div>
      )}

      {/* Layer */}
      <div className="corgee-layout-tableSubmitTimet">
        <div className="corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime">
          <span>Layer</span>
        </div>
        <div className="corgee-layout-table-cellSubmitTimet">
          <Select<Option>
            {...commonProps}
            instanceId={`layer-${customKey}`}
            options={layerOptions}
            value={selectedLayerOption}
            onChange={(opt: SingleValue<Option>) => onLayerChange(opt?.data ?? null)}
            isDisabled={valForm.disabledLayers || isLoadingLayers}
            isLoading={isLoadingLayers}
            placeholder=""
          />
          {valForm.selectedId && (
            <span className="submit-required-field" style={{ fontSize: '11px' }}>
              Layer is required
            </span>
          )}
        </div>
      </div>

      {/* Phases */}
      {!valForm.showTask && phasesBlock}

      {/* Project */}
      {valForm.showProjects && (
        <div className="corgee-layout-tableSubmitTimet">
          <div className="corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime">
            <span>Project</span>
          </div>
          <div className="corgee-layout-table-cellSubmitTimet">
            <Select<Option>
              {...commonProps}
              instanceId={`project-${customKey}`}
              options={projectOptions}
              value={selectedProjectOption}
              onChange={(opt: SingleValue<Option>) => onProjectChange(opt?.data ?? null)}
              isDisabled={valForm.disabledProject || isLoadingProjects}
              isLoading={isLoadingProjects}
              placeholder=""
            />
            {valForm.validProject && (
              <span className="submit-required-field" style={{ fontSize: '11px' }}>
                Project is required
              </span>
            )}
          </div>
        </div>
      )}

      {/* Type */}
      {valForm.showType && (
        <div className="corgee-layout-tableSubmitTimet">
          <div className="corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime">
            <span>Type</span>
          </div>
          <div className="corgee-layout-table-cellSubmitTimet">
            <Select<Option>
              {...commonProps}
              instanceId={`type-${customKey}`}
              options={typeOptions}
              value={selectedTypeOption}
              onChange={(opt: SingleValue<Option>) => onTypeChange(opt?.data ?? null)}
              isDisabled={valForm.disabledType || isLoadingTypes}
              isLoading={isLoadingTypes}
              placeholder=""
            />
            {valForm.validType && (
              <span className="submit-required-field" style={{ fontSize: '11px' }}>
                Type is required
              </span>
            )}
          </div>
        </div>
      )}

      {/* Task */}
      {valForm.showTask && (
        <div className="corgee-layout-tableSubmitTimet">
          <div className="corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime">
            <span>Task</span>
          </div>
          <div className="corgee-layout-table-cellSubmitTimet">
            <Select<Option>
              {...commonProps}
              instanceId={`task-${customKey}`}
              options={taskOptions}
              value={selectedTaskOption}
              onChange={(opt: SingleValue<Option>) => onTaskChange(opt?.data ?? null)}
              isDisabled={valForm.disabledTask || isLoadingTasks}
              isLoading={isLoadingTasks}
              placeholder=""
            />
            {valForm.validTask && (
              <span className="submit-required-field" style={{ fontSize: '11px' }}>
                Task is required
              </span>
            )}
          </div>
        </div>
      )}

      {valForm.showTask && phasesBlock}
    </div>
  );
};

export default SelectorsSection;
